import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, RotateCcw, ShieldCheck, EyeOff, RefreshCcw } from "lucide-react";

const MODULE_LABELS = {
  dashboard_charts: "Dashboard Charts (procurement value, spend charts)",
  quotations: "Quotation Comparison module",
  invoices: "Invoices module",
  pvs: "Payment Vouchers module",
  users: "Users management",
  audit: "Audit Log",
  po_pdf: "Purchase Order PDF (download & preview)",
};

const ENTITY_LABELS = {
  indent: "Indent",
  po: "Purchase Order",
  grn: "Goods Receipt",
  invoice: "Invoice",
  pv: "Payment Voucher",
  dashboard: "Dashboard KPIs",
  quotation: "Quotation Comparison",
};

const FIELD_LABELS = {
  "items.estimated_cost": "Line item — Estimated Cost",
  "subtotal": "Subtotal",
  "gst_total": "GST Total",
  "grand_total": "Grand Total",
  "freight": "Freight",
  "other_charges": "Other Charges",
  "items.rate": "Line item — Rate",
  "items.amount": "Line item — Amount",
  "items.gst_pct": "Line item — GST %",
  "items.gst_amount": "Line item — GST Amount",
  "items.total": "Line item — Total",
  "invoice_amount": "Invoice Amount",
  "gst_amount": "GST Amount",
  "three_way_match": "3-way match indicator",
  "match_details": "Match diff details",
  "tds_amount": "TDS Amount",
  "tds_pct": "TDS %",
  "retention_amount": "Retention Amount",
  "retention_pct": "Retention %",
  "net_payable": "Net Payable",
  "bank": "Bank",
  "payment_mode": "Payment Mode",
  "utr_no": "UTR No.",
  "cheque_no": "Cheque No.",
  "procurement_value": "Total Procurement Value",
  "paid_total": "Paid to Vendors",
  "top_vendors": "Top Vendors chart",
  "project_spend": "Project-wise spend chart",
  "monthly_spend": "Monthly spend chart",
  "items.vendors.rate": "Vendor quote — Rate",
  "items.vendors.gst_pct": "Vendor quote — GST %",
  "items.vendors.freight": "Vendor quote — Freight",
};

export default function Settings() {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("Indent Creator");
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings/field-visibility");
    setData(data);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const cfg = data.config[role] || { hide_modules: [], hide_fields: {} };

  const toggleModule = (mod) => {
    const set = new Set(cfg.hide_modules || []);
    if (set.has(mod)) set.delete(mod); else set.add(mod);
    setData({ ...data, config: { ...data.config, [role]: { ...cfg, hide_modules: [...set] } } });
    setDirty(true);
  };

  const toggleField = (entity, field) => {
    const current = new Set(cfg.hide_fields?.[entity] || []);
    if (current.has(field)) current.delete(field); else current.add(field);
    const nextHF = { ...(cfg.hide_fields || {}) };
    if (current.size === 0) delete nextHF[entity];
    else nextHF[entity] = [...current];
    setData({ ...data, config: { ...data.config, [role]: { ...cfg, hide_fields: nextHF } } });
    setDirty(true);
  };

  const save = async () => {
    try {
      await api.put("/settings/field-visibility", { config: data.config });
      toast.success("Visibility rules saved. Log out and log back in as an affected user to see changes.");
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Restore the shipped defaults for every role? This wipes all customisations.")) return;
    try {
      await api.post("/settings/field-visibility/reset");
      toast.success("Defaults restored");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const totalHiddenForRole = (r) => {
    const c = data.config[r] || {};
    const fieldCount = Object.values(c.hide_fields || {}).reduce((a, b) => a + b.length, 0);
    return (c.hide_modules?.length || 0) + fieldCount;
  };

  return (
    <div data-testid="settings-visibility-page">
      <PageHeader
        title="Field Visibility Matrix"
        subtitle="Choose exactly which fields and modules each role is allowed to see. Applied server-side."
        actions={
          <>
            <Button variant="outline" onClick={resetToDefaults} data-testid="reset-defaults-btn"><RefreshCcw size={14} className="mr-2" />Restore Defaults</Button>
            <Button variant="outline" onClick={load} data-testid="reset-btn"><RotateCcw size={14} className="mr-2" />Discard Changes</Button>
            <PrimaryButton onClick={save} disabled={!dirty} data-testid="save-visibility-btn"><Save size={14} className="mr-2" />Save Changes</PrimaryButton>
          </>
        }
      />

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <Card className="p-4 border-border h-fit">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
            <ShieldCheck size={12} /> Select Role
          </div>
          <div className="space-y-1">
            {data.roles.map((r) => {
              const count = totalHiddenForRole(r);
              return (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  data-testid={`role-tab-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-all flex items-center justify-between ${
                    role === r ? "bg-kabir text-zinc-900 font-semibold" : "hover:bg-secondary/70"
                  }`}
                >
                  <span>{r}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${role === r ? "bg-zinc-900/10" : "bg-kabir/20 text-kabir"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1 flex items-center gap-2">
              <EyeOff size={12} /> Modules Hidden from
            </div>
            <div className="font-display text-xl font-semibold mb-4">{role}</div>
            <div className="grid md:grid-cols-2 gap-2">
              {data.modules.map((m) => (
                <label
                  key={m}
                  className="flex items-start gap-2.5 p-2.5 rounded-md hover:bg-secondary/50 cursor-pointer border border-transparent hover:border-border"
                  data-testid={`module-${m}`}
                >
                  <Checkbox
                    checked={(cfg.hide_modules || []).includes(m)}
                    onCheckedChange={() => toggleModule(m)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{MODULE_LABELS[m] || m}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{m}</div>
                  </div>
                </label>
              ))}
            </div>
          </Card>

          <Tabs defaultValue={Object.keys(data.available_fields)[0]}>
            <TabsList className="flex-wrap h-auto" data-testid="entity-tabs">
              {Object.keys(data.available_fields).map((ent) => {
                const count = cfg.hide_fields?.[ent]?.length || 0;
                return (
                  <TabsTrigger key={ent} value={ent} data-testid={`entity-tab-${ent}`}>
                    {ENTITY_LABELS[ent] || ent}
                    {count > 0 && <span className="ml-1.5 text-[10px] font-mono bg-kabir/20 text-kabir px-1.5 rounded">{count}</span>}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {Object.entries(data.available_fields).map(([ent, fields]) => (
              <TabsContent key={ent} value={ent}>
                <Card className="p-5 border-border">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
                    Fields hidden on {ENTITY_LABELS[ent] || ent}
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {fields.map((f) => (
                      <label
                        key={f}
                        className="flex items-center gap-2.5 p-2 rounded-md hover:bg-secondary/50 cursor-pointer"
                        data-testid={`field-${ent}-${f.replace(/\./g, "-")}`}
                      >
                        <Checkbox
                          checked={(cfg.hide_fields?.[ent] || []).includes(f)}
                          onCheckedChange={() => toggleField(ent, f)}
                        />
                        <div className="flex-1 text-sm">
                          <div>{FIELD_LABELS[f] || f}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{f}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
